<template>
  <MysticalCard size="default" hoverable class="naming-ai-report">
    <template #header>
      <div class="ai-report-header">
        <span>✨ 综合评分 · AI 取名报告</span>
      </div>
    </template>

    <!-- ========== 综合评分模块 ========== -->
    <div v-if="scoreResult" class="score-module">
      <div class="score-hero">
        <el-progress
          type="dashboard"
          :percentage="scoreResult.scores.total"
          :width="132"
          :stroke-width="10"
          :color="scoreColor"
        >
          <template #default>
            <div class="score-total">
              <span class="score-number">{{ scoreResult.scores.total }}</span>
              <span class="score-label">{{ scoreLabel }}</span>
            </div>
          </template>
        </el-progress>
        <div class="score-hero-side">
          <p class="score-name">{{ surname }}{{ givenName }}</p>
          <p class="score-sub">三才 {{ sancaiCombo || '—' }} · {{ scoreResult.sancaiLevel || '配置未知' }}</p>
          <p class="score-sub">{{ gridsLine }}</p>
        </div>
      </div>

      <div class="score-dims">
        <div v-for="dim in dimList" :key="dim.key" class="score-dim">
          <span class="dim-name">{{ dim.name }}</span>
          <el-progress
            class="dim-bar"
            :percentage="dim.pct"
            :stroke-width="12"
            :color="dimColor(dim.pct)"
          />
          <span class="dim-value" :class="{ locked: dim.locked }">{{ dim.text }}</span>
        </div>
      </div>

      <p class="score-note">
        {{ birthZodiac ? '八字喜用需完整排盘，已交由 AI 报告解读。' : '填写下方出生信息可解锁生肖维度；八字喜用维度由 AI 报告解读。' }}
      </p>
    </div>

    <!-- ========== 出生信息（可选） ========== -->
    <el-collapse v-model="openedBirth" class="birth-collapse">
      <el-collapse-item name="birth" title="🎂 出生信息（选填，用于生肖维度与 AI 解读）">
        <div class="birth-form">
          <div class="birth-field">
            <span class="birth-label">性别</span>
            <el-radio-group v-model="gender" size="small">
              <el-radio-button value="男">男</el-radio-button>
              <el-radio-button value="女">女</el-radio-button>
            </el-radio-group>
          </div>
          <div class="birth-field">
            <span class="birth-label">出生时间</span>
            <el-date-picker
              v-model="birthDate"
              type="datetime"
              placeholder="如 2000-05-20 14:00"
              format="YYYY-MM-DD HH:mm"
              value-format="YYYY-MM-DD HH:mm"
              :default-time="defaultBirthTime"
            />
          </div>
          <p v-if="birthZodiac" class="birth-zodiac">已按出生年份识别生肖：{{ birthZodiac }}</p>
        </div>
      </el-collapse-item>
    </el-collapse>

    <!-- ========== AI 报告 ========== -->
    <div class="ai-section">
      <div class="ai-entry">
        <MysticalButton :loading="analyzing" @click="handleAnalyze">
          {{ analyzing ? '解读生成中…' : '✨ 生成 AI 取名报告' }}
        </MysticalButton>
        <span class="ai-entry-hint">{{ userStore.isLoggedIn ? '消耗灵石（以页面计价为准）' : '登录后可用 · 消耗灵石（以页面计价为准）' }}</span>
        <!-- 历史入口常驻（不依赖 report 存在，刷新/换名后仍可回看） -->
        <el-button link size="small" class="ai-entry-history" @click="openHistory">🕘 历史报告</el-button>
      </div>

      <div v-if="report" class="report-content">
        <div class="report-block">
          <h5 class="report-title">📜 总评</h5>
          <p class="report-text">{{ report.overall_summary }}</p>
        </div>
        <div class="report-block">
          <h5 class="report-title">🧭 五格三才解读</h5>
          <p class="report-text">{{ report.wuge_sancai_reading }}</p>
        </div>
        <div class="report-block">
          <h5 class="report-title">🔤 字义与音韵</h5>
          <p class="report-text">{{ report.meaning_reading }}</p>
        </div>
        <div v-if="report.zodiac_tip" class="report-block">
          <h5 class="report-title">🐉 生肖提示</h5>
          <p class="report-text">{{ report.zodiac_tip }}</p>
        </div>
        <div v-if="report.highlights && report.highlights.length" class="report-block">
          <h5 class="report-title">🌟 亮点</h5>
          <div class="report-tags">
            <el-tag v-for="item in report.highlights" :key="item" type="success" effect="light">{{ item }}</el-tag>
          </div>
        </div>
        <div v-if="report.caveats && report.caveats.length" class="report-block">
          <h5 class="report-title">⚖️ 可商榷之处</h5>
          <div class="report-tags">
            <el-tag v-for="item in report.caveats" :key="item" type="warning" effect="light">{{ item }}</el-tag>
          </div>
        </div>
        <div v-if="report.advice" class="report-block">
          <h5 class="report-title">💡 建议</h5>
          <p class="report-text">{{ report.advice }}</p>
        </div>

        <div class="report-actions">
          <el-button type="primary" @click="posterVisible = true">🎴 生成分享卡片</el-button>
          <el-button type="primary" @click="shareVisible = true">📣 分享结果</el-button>
          <el-button @click="openHistory">🕘 历史报告</el-button>
        </div>
      </div>
    </div>

    <!-- ========== 历史报告 ========== -->
    <el-dialog v-model="historyVisible" title="历史取名报告" width="480px">
      <div v-loading="historyLoading" class="history-list">
        <div v-if="!historyLoading && !historyList.length" class="history-empty">暂无历史报告</div>
        <div
          v-for="item in historyList"
          :key="item.id"
          class="history-item"
          :class="{ 'is-full': item.report }"
          @click="viewHistory(item)"
        >
          <span class="history-name">{{ item.surname }}{{ item.given_name }}</span>
          <span class="history-time">{{ formatHistoryTime(item.created_at) }}</span>
        </div>
      </div>
    </el-dialog>

    <!-- ========== 支付与分享弹层 ========== -->
    <PayDialog
      v-model:visible="showPayDialog"
      :cost="payDialogCost"
      :balance="payDialogBalance"
      :feature="payDialogFeature"
      :rmb-price="payDialogRmbPrice"
      @goPoints="goToPoints"
    />
    <PosterDialog v-model="posterVisible" type="naming" :data="posterData" />
    <ShareResult
      v-model:visible="shareVisible"
      scene-label="AI 取名"
      :headline="shareHeadline"
      :subline="shareSubline"
      :tags="shareTags"
      :symbol="shareSymbol"
      page="naming"
    />
  </MysticalCard>
</template>

<script setup>
import { ref, computed } from 'vue'
import { ElMessage } from 'element-plus'
import lunisolar from 'lunisolar'
import zhCn from 'lunisolar/locale/zh-cn'
import { char8ex } from '@lunisolar/plugin-char8ex'
import MysticalCard from '@/components/common/MysticalCard.vue'
import MysticalButton from '@/components/common/MysticalButton.vue'
import PayDialog from '@/components/common/PayDialog.vue'
import PosterDialog from '@/components/poster/PosterDialog.vue'
import { ShareResult } from '@/components/common'
import { useAuthGuard } from '@/composables/useAuthGuard'
import { usePaymentGuard } from '@/composables/usePaymentGuard'
import { useUserStore } from '@/stores/user'
import { lookupChar } from '@/utils/kangxiDict'
import { scoreNameByGrids } from '@/utils/namingScore'
import { analyzeNaming, getNamingHistory } from '@/services/namingService'

lunisolar.locale(zhCn)
lunisolar.extend(char8ex)

const props = defineProps({
  /** 姓氏（单字） */
  surname: { type: String, required: true },
  /** 名（1-2 字） */
  givenName: { type: String, required: true },
  /** 姓氏康熙笔画（可能是手动输入） */
  surnameStrokes: { type: Number, required: true },
  /** 名字逐字康熙笔画 */
  givenStrokes: { type: Array, required: true },
  /** 五格 {tianGe,renGe,diGe,waiGe,zongGe} */
  grids: { type: Object, required: true },
  /** 三才组合，如 '木木火' */
  sancaiCombo: { type: String, default: '' }
})

const { requireLogin } = useAuthGuard()
const {
  showPayDialog, payDialogCost, payDialogBalance, payDialogFeature,
  payDialogRmbPrice, checkFeatureCost, goToPoints
} = usePaymentGuard()
const userStore = useUserStore()

/* ---------- 出生信息（可选） ---------- */
const openedBirth = ref([])
const gender = ref('')
const birthDate = ref('')
const defaultBirthTime = new Date(2000, 0, 1, 12, 0, 0)

/** 由出生时间推生肖（lunisolar 年支，自动处理立春换年） */
const birthZodiac = computed(() => {
  if (!birthDate.value) return null
  try {
    const branch = lunisolar(birthDate.value).char8.year.branch.name
    const map = { 子: '鼠', 丑: '牛', 寅: '虎', 卯: '兔', 辰: '龙', 巳: '蛇', 午: '马', 未: '羊', 申: '猴', 酉: '鸡', 戌: '狗', 亥: '猪' }
    return map[branch] || null
  } catch {
    return null
  }
})

/* ---------- 综合评分 ---------- */
const scoreResult = computed(() => {
  if (!props.grids) return null
  return scoreNameByGrids({
    grids: props.grids,
    sancaiCombo: props.sancaiCombo,
    givenChars: [...props.givenName],
    zodiac: birthZodiac.value
  })
})

const scoreLabel = computed(() => {
  const t = scoreResult.value?.scores.total ?? 0
  if (t >= 90) return '极佳'
  if (t >= 80) return '优秀'
  if (t >= 70) return '良好'
  if (t >= 60) return '中等'
  return '待改善'
})
const scoreColor = computed(() => dimColor(scoreResult.value?.scores.total ?? 0))
const gridsLine = computed(() => {
  const g = props.grids
  return `天${g.tianGe} · 人${g.renGe} · 地${g.diGe} · 外${g.waiGe} · 总${g.zongGe}`
})

const dimList = computed(() => {
  const s = scoreResult.value?.scores
  if (!s) return []
  const mk = (key, name) => {
    const v = s[key]
    const locked = v === null || v === undefined
    return {
      key, name,
      pct: locked ? 0 : Math.max(2, Math.min(100, v)),
      text: locked ? '待解锁' : `${v}分`,
      locked
    }
  }
  return [mk('shuli', '五格数理'), mk('sancai', '三才配置'), mk('bazi', '八字喜用'), mk('zodiac', '生肖宜忌')]
})

function dimColor(score) {
  if (score >= 80) return 'var(--fh-success)'
  if (score >= 60) return 'var(--fh-gold)'
  return 'var(--fh-danger)'
}

/* ---------- AI 报告 ---------- */
const analyzing = ref(false)
const report = ref(null)

/** 逐字数理明细：优先字典（康熙+五行），未收录字回退手动笔画 */
const charDetails = computed(() => {
  const chars = [props.surname, ...props.givenName]
  const strokes = [props.surnameStrokes, ...props.givenStrokes]
  return chars.map((char, i) => {
    const d = lookupChar(char)
    return { char, strokes: d ? d.strokes : strokes[i], wuxing: d ? d.wuxing : null }
  })
})

async function handleAnalyze() {
  if (analyzing.value) return // 防双击/防重复触发（守卫 await 期间的竞态窗口）
  if (!requireLogin('naming-ai')) return
  const canProceed = await checkFeatureCost('naming', 'AI取名报告')
  if (!canProceed) return
  analyzing.value = true
  try {
    const payload = {
      surname: props.surname,
      given_name: props.givenName,
      gender: gender.value || null,
      birth_info: birthDate.value || null,
      grids: {
        tian: props.grids.tianGe, ren: props.grids.renGe, di: props.grids.diGe,
        wai: props.grids.waiGe, zong: props.grids.zongGe
      },
      sancai_combo: props.sancaiCombo,
      char_details: charDetails.value,
      scores: scoreResult.value?.scores ?? null
    }
    const res = await analyzeNaming(payload)
    const data = res?.data || res
    report.value = data?.report || null
    if (!report.value) throw new Error('报告数据为空')
    userStore.fetchPointInfo() // 刷新导航栏余额（不阻塞展示）
    ElMessage.success('AI 取名报告已生成')
  } catch (e) {
    ElMessage.error(e?.message || '报告生成失败，请稍后再试')
  } finally {
    analyzing.value = false
  }
}

/* ---------- 历史报告 ---------- */
const historyVisible = ref(false)
const historyLoading = ref(false)
const historyList = ref([])

async function openHistory() {
  if (!requireLogin('naming-history')) return
  historyVisible.value = true
  historyLoading.value = true
  try {
    const res = await getNamingHistory()
    const data = res?.data || res
    historyList.value = Array.isArray(data) ? data : data?.items || []
  } catch (e) {
    ElMessage.error(e?.message || '历史记录加载失败')
  } finally {
    historyLoading.value = false
  }
}

function viewHistory(item) {
  if (item?.report) {
    report.value = item.report
    historyVisible.value = false
  } else {
    // 列表仅首条带全量报告（接口设计省流量），点其余条目给明确反馈而非无反应
    ElMessage.info('该记录暂不支持完整回看，可重新生成同姓名报告')
  }
}

function formatHistoryTime(t) {
  if (!t) return ''
  return String(t).replace('T', ' ').slice(0, 16)
}

/* ---------- 分享卡片 ---------- */
const posterVisible = ref(false)

/* ---------- 结果分享卡（ShareResult, 文案取已有评分 state, 不发新请求） ---------- */
const shareVisible = ref(false)
const shareHeadline = computed(() => `我测了名字「${props.surname}${props.givenName}」的吉凶评分`)
const shareSubline = computed(() => {
  const s = scoreResult.value?.scores
  if (!s) return 'AI 取名报告解读'
  const parts = [`综合 ${s.total} 分`, scoreLabel.value]
  if (props.sancaiCombo) parts.push(`三才 ${props.sancaiCombo}`)
  return parts.join(' · ')
})
const shareTags = computed(() => {
  const s = scoreResult.value?.scores
  if (!s) return []
  return [
    `五格数理 ${s.shuli ?? '—'}分`,
    `三才配置 ${s.sancai ?? '—'}分`,
    `生肖宜忌 ${s.zodiac ?? '—'}分`
  ]
})
// 视觉主体: 综合评分大字(如「87分」), 无评分时退回姓名
const shareSymbol = computed(() => {
  const t = scoreResult.value?.scores?.total
  if (t !== undefined && t !== null) return `${t}分`
  return props.givenName ? `${props.surname}${props.givenName}` : ''
})
/** 二维码：走后端 share/qrcode 端点（与 InvitePoster 同一模式），未登录时留空 */
const qrcodeUrl = computed(() => {
  const token = userStore.token
  if (!token) return ''
  const base = (import.meta.env.VITE_API_BASE || window.location.origin) + '/api'
  return `${base}/share/qrcode?page=naming&size=430&token=${encodeURIComponent(token)}`
})
const posterData = computed(() => {
  const s = scoreResult.value?.scores
  const barW = (v) => String(Math.round(300 * ((v ?? 0) / 100)))
  return {
    fullName: `${props.surname}${props.givenName}`,
    totalScore: s ? String(s.total) : '—',
    scoreLabel: scoreLabel.value,
    sancaiCombo: props.sancaiCombo || '—',
    gridsLine: gridsLine.value,
    shuliScore: s?.shuli != null ? `${s.shuli}` : '待解锁',
    sancaiScore: s?.sancai != null ? `${s.sancai}` : '待解锁',
    baziScore: '待解锁',
    zodiacScore: s?.zodiac != null ? `${s.zodiac}` : '待解锁',
    shuliBarWidth: barW(s?.shuli),
    sancaiBarWidth: barW(s?.sancai),
    zodiacBarWidth: barW(s?.zodiac),
    // ⚠️ 合规：海报只显示昵称，绝不能露出手机号/账号（nickname 可能被注册逻辑填成手机号）
    nickname: (() => {
      const n = (userStore.user?.nickname || '').trim()
      if (!n || /^1[3-9]\d{9}$/.test(n) || /^\d{5,}$/.test(n)) return '随心用户'
      return n
    })(),
    qrcodeUrl: qrcodeUrl.value
  }
})
</script>

<style scoped>
.naming-ai-report {
  margin-top: 20px;
}

/* ---------- 综合评分 ---------- */
.score-module {
  padding: 8px 4px 4px;
}
.score-hero {
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
}
.score-total {
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1.2;
}
.score-number {
  font-size: 34px;
  font-weight: 700;
  color: var(--fh-text);
}
.score-label {
  font-size: 13px;
  color: var(--fh-text-secondary);
}
.score-hero-side {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.score-name {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: var(--fh-text);
  letter-spacing: 4px;
}
.score-sub {
  margin: 0;
  font-size: 13px;
  color: var(--fh-text-secondary);
}
.score-dims {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.score-dim {
  display: flex;
  align-items: center;
  gap: 12px;
}
.dim-name {
  width: 72px;
  flex-shrink: 0;
  font-size: 14px;
  color: var(--fh-text);
}
.dim-bar {
  flex: 1;
}
.dim-bar :deep(.el-progress-bar__outer) {
  background: var(--fh-accent-light);
}
.dim-value {
  width: 56px;
  flex-shrink: 0;
  text-align: right;
  font-size: 14px;
  font-weight: 600;
  color: var(--fh-text);
}
.dim-value.locked {
  color: var(--fh-text-muted);
  font-weight: 400;
}
.score-note {
  margin: 12px 0 0;
  font-size: 12px;
  color: var(--fh-text-muted);
}

/* ---------- 出生信息 ---------- */
.birth-collapse {
  margin-top: 12px;
  border-top: 1px solid var(--fh-border);
  border-bottom: 1px solid var(--fh-border);
}
.birth-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 4px 0 8px;
}
.birth-field {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.birth-label {
  font-size: 14px;
  color: var(--fh-text-secondary);
}
.birth-zodiac {
  margin: 0;
  font-size: 13px;
  color: var(--fh-accent);
}

/* ---------- AI 报告 ---------- */
.ai-section {
  margin-top: 16px;
}
.ai-entry {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.ai-entry-hint {
  font-size: 12px;
  color: var(--fh-text-muted);
}
/* 历史入口推到行尾 */
.ai-entry-history {
  margin-left: auto;
}
.report-content {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.report-block {
  padding: 12px 14px;
  background: var(--fh-card-bg);
  border: 1px solid var(--fh-border);
  border-radius: 10px;
}
.report-title {
  margin: 0 0 8px;
  font-size: 15px;
  color: var(--fh-accent);
}
.report-text {
  margin: 0;
  font-size: 14px;
  line-height: 1.8;
  color: var(--fh-text);
  white-space: pre-wrap;
}
.report-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.report-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

/* ---------- 历史报告 ---------- */
.history-list {
  max-height: 360px;
  overflow-y: auto;
}
.history-empty {
  padding: 32px 0;
  text-align: center;
  color: var(--fh-text-muted);
  font-size: 14px;
}
.history-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
}
.history-item:hover {
  background: var(--fh-card-bg-hover);
}
/* 首条带全量报告可完整回看，视觉上区别于普通条目 */
.history-item.is-full .history-name {
  font-weight: 600;
}
.history-name {
  font-size: 15px;
  font-weight: 600;
  color: var(--fh-text);
}
.history-time {
  font-size: 12px;
  color: var(--fh-text-muted);
}

/* ---------- 移动端 ---------- */
@media (max-width: 768px) {
  .score-hero {
    gap: 16px;
  }
  .score-name {
    font-size: 18px;
    letter-spacing: 2px;
  }
  .dim-name {
    width: 60px;
    font-size: 13px;
  }
  .dim-value {
    width: 48px;
    font-size: 13px;
  }
  .report-actions {
    flex-direction: column;
  }
  .report-actions :deep(.el-button) {
    width: 100%;
    margin-left: 0;
  }
}
</style>
